/**
 * Phosphor Icons System
 * 
 * Rustig, professioneel icon systeem met zachte antraciet tinten
 * Vervangt: Lucide Sparkles, Bot, Brain, Wand2, emoji's
 */

import type { IconWeight } from 'phosphor-react';
import {
  Article,
  ChatCircle,
  Handshake,
  Lightning,
  FileText,
  Sparkle,
  Users,
  User,
  UserCircle,
  ChartBar,
  ChartLine,
  Timer,
  Clock,
  Calendar,
  Chat,
  EnvelopeSimple,
  Phone,
  VideoCamera,
  Pencil,
  Plus,
  Trash,
  Check,
  X,
  Flower,
  Brain,
  Heart,
  Target,
  TrendUp,
  Flag,
  Gear,
  MagnifyingGlass,
  Bell,
  Share,
  Download,
  Upload,
  CheckCircle,
  Warning,
  Info,
  XCircle,
  Briefcase,
  Buildings,
  Tag,
  Link,
  Medal,
  Smiley,
  ImageSquare,
  Paperclip,
  LinkedinLogo,
  FacebookLogo,
  TwitterLogo,
  InstagramLogo,
  ShareNetwork,
  PencilSimpleLine,
  Prohibit,
  WarningCircle,
  CheckCircle as PhosphorCheckCircle,
  NotePencil,
  PinterestLogo,
  Lightbulb,
  Newspaper,
  Diamond,
  ListBullets,
  ArchiveTray,
  HourglassSimple,
  RocketLaunch,
  ArrowsClockwise,
  Trophy,
  Eye,
  FloppyDisk,
  FolderSimple,
  ClipboardText,
  Pause,
  Play,
  Fire,
  Package,
  Confetti,
  FilmSlate,
  Crosshair,
  MapPin,
  Lock,
  GlobeSimple,
  Microphone,
  PhoneDisconnect,
  Megaphone,
  CalendarPlus,
  UserPlus,
  House,
  QrCode,
  ChartBar as ChartBarAlt,
  SlidersHorizontal,
  ArrowRight,
  Rocket,
  CornersOut,
  CornersIn,
  VideoCamera as VideoCameraIcon,
  ProjectorScreen,
} from 'phosphor-react';

export type { IconWeight };

// ============================================================================
// ICON EXPORTS - Semantische namen
// ============================================================================

/**
 * Herbruikbare Phosphor icons met semantische namen
 * Gebruik deze in plaats van Lucide's tech-heavy icons
 */

// AI/Assistant features (was: Sparkles, Bot, Brain, Wand2)
export const InsightIcon = Article;           // Voor inzichten, analyses (was: Brain, Sparkles)
export const AssistantIcon = ChatCircle;      // Voor AI assistant (was: Bot)
export const RelationIcon = Handshake;        // Voor relation manager (was: 🤖 emoji)
export const QuickIcon = Lightning;           // Voor snelle acties (was: Zap, ✨)
export const DocumentIcon = FileText;         // Voor documenten, samenvattingen
export const AccentIcon = Sparkle;            // Alleen voor rewards/credits (zeer beperkt gebruik!)

// Navigation & UI
export const CommunityIcon = Users;           // Community, groepen
export const ProfileIcon = User;              // Profiel, persoon
export const AvatarIcon = UserCircle;         // Avatar, gebruiker cirkel

// Analytics & Data
export const AnalyticsIcon = ChartBar;        // Analytics, statistieken (was: 📊)
export const TrendIcon = ChartLine;           // Trends, groei

// Time & Planning
export const TimeIcon = Timer;                // Time tracking (was: ⏱️)
export const ClockIcon = Clock;               // Tijden, timestamps
export const CalendarIcon = Calendar;         // Agenda, planning

// Communication
export const ChatIcon = Chat;                 // Chat, berichten (was: 💬)
export const MailIcon = EnvelopeSimple;       // Email, inbox
export const PhoneIcon = Phone;               // Bellen
export const VideoIcon = VideoCamera;         // Video calls

// Content
export const EditIcon = Pencil;               // Bewerken
export const AddIcon = Plus;                  // Toevoegen
export const DeleteIcon = Trash;              // Verwijderen
export const CheckIcon = Check;               // Bevestigen
export const CloseIcon = X;                   // Sluiten

// Mindfulness & Wellness
export const MindfulIcon = Flower;            // Mindfulness (was: 🧘‍♀️)
export const ThinkIcon = Brain;               // Cognitive (was: 🧠) - alleen als echt nodig
export const WellnessIcon = Heart;            // Wellness, gezondheid

// Goals & Growth
export const GoalIcon = Target;               // Doelen (was: 🎯)
export const GrowthIcon = TrendUp;            // Groei, vooruitgang
export const MilestoneIcon = Flag;            // Mijlpalen

// Actions & Tools
export const SettingsIcon = Gear;             // Instellingen
export const SearchIcon = MagnifyingGlass;    // Zoeken
export const NotificationIcon = Bell;         // Notificaties
export const ShareIcon = Share;               // Delen
export const DownloadIcon = Download;         // Downloaden
export const UploadIcon = Upload;             // Uploaden

// Status & Indicators
export const SuccessIcon = CheckCircle;       // Success
export const WarningIcon = Warning;           // Waarschuwing
export const InfoIcon = Info;                 // Informatie
export const ErrorIcon = XCircle;             // Error

// Business & Professional
export const BusinessIcon = Briefcase;        // Business, werk
export const CompanyIcon = Buildings;         // Bedrijf, organisatie
export const PartnerIcon = Handshake;         // Partners, samenwerking
export const TagIcon = Tag;                  // Tags, labels
export const ConnectionLinkIcon = Link;       // Links, connecties, koppelingen
export const ExpertIcon = Medal;             // Expert status, achievements

// Feed Input Actions (vervangt emoji's in post creator)
export const EmojiIcon = Smiley;             // Emoji picker (was: 😊)
export const ImageIcon = ImageSquare;        // Afbeelding upload (was: 🖼️)
export const AttachIcon = Paperclip;         // Bestand bijvoegen (was: 📄)

// Social Media Platforms (vervangt emoji's in social sharing)
export const LinkedInIcon = LinkedinLogo;    // LinkedIn (was: 💼)
export const FacebookIcon = FacebookLogo;    // Facebook (was: 📘)
export const TwitterIcon = TwitterLogo;      // Twitter/X (was: 🐦)
export const InstagramIcon = InstagramLogo;  // Instagram (was: 📸)
export const ShareOutIcon = ShareNetwork;    // Deel ook naar (was: 📤)

// Post Limit Indicators (vervangt emoji's in PostLimitIndicator)
export const PostIcon = NotePencil;          // Post schrijven (was: 📝)
export const LimitIcon = Prohibit;           // Limiet bereikt (was: 🚫)
export const LimitWarningIcon = WarningCircle; // Bijna bij limiet (was: ⚠️)
export const UnlimitedIcon = PhosphorCheckCircle; // Onbeperkt (was: ✅)

// Social Hub (vervangt emoji's in Social Media Management Center)
export const PinterestIcon = PinterestLogo;  // Pinterest (was: 📌)
export const TipIcon = Lightbulb;            // Tips, hints (was: 💡)
export const NewsFeedIcon = Newspaper;       // NewsFeed, nieuws (was: 📰)
export const PremiumIcon = Diamond;          // Premium, upgrade (was: 💎)
export const QueueIcon = ListBullets;        // Queue, lijst (was: 📋)
export const EmptyTrayIcon = ArchiveTray;    // Lege inbox (was: 📭)
export const HourglassIcon = HourglassSimple; // Laden, wachten (was: ⏳)
export const LaunchIcon = RocketLaunch;      // Publiceren, lanceren (was: 🚀)
export const RetryIcon = ArrowsClockwise;    // Opnieuw proberen (was: 🔄)
export const TrophyIcon = Trophy;            // Best presterende (was: 🏆)
export const ViewIcon = Eye;                 // Impressies, views (was: 👁️)
export const SaveDiskIcon = FloppyDisk;      // Opslaan (was: 💾)
export const FolderIcon = FolderSimple;      // Alle items, archief (was: 🗂️)
export const ClipboardIcon = ClipboardText;  // Clipboard, content (was: 📋)
export const PauseIcon = Pause;              // Pauze (was: ⏸️)
export const PlayIcon = Play;                // Afspelen (was: ▶️)
export const FireIcon = Fire;                // Urgent, brand (was: 🔥)
export const PackageIcon = Package;          // Pakket, box (was: 📦)
export const CelebrationIcon = Confetti;     // Feest, succes (was: 🎉)
export const FilmIcon = FilmSlate;           // Film, video (was: 🎬)
export const CrosshairIcon = Crosshair;      // Precies doel (was: 🎯 variant)
export const ProjectorScreenIcon = ProjectorScreen; // Fysieke bijeenkomst / beamer

// Location & Navigation (vervangt emoji's in club hub pages)
export const MapPinIcon = MapPin;            // Locatie/regio (was: 📍)
export const LockIcon = Lock;               // Besloten groep (was: 🔒)
export const GlobeIcon = GlobeSimple;       // Open/publiek (was: 🌐)
export const MicrophoneIcon = Microphone;   // Meeting starten (was: 🎙)
export const EndCallIcon = PhoneDisconnect; // Meeting verlaten (was: 📴)
export const AnnouncementIcon = Megaphone;  // Geen posts / aankondiging (was: 📣)
export const CalendarPlusIcon = CalendarPlus; // Event aanmaken (was: 📅+)
export const InviteIcon = UserPlus;         // Uitnodigen (was: 🔗/👥)
export const HomeIcon = House;              // Home (was: 🏠)
export const QrCodeIcon = QrCode;           // QR code (was: 🔲)
export const FiltersIcon = SlidersHorizontal; // Filters, instellingen
export const NextIcon = ArrowRight;         // Volgende, doorgaan
export const ActivationIcon = Rocket;       // Activatie/launch (was: 🚀)

// ============================================================================
// ICON STYLES - Zachte antraciet tinten
// ============================================================================

export const ICON_STYLES = {
  /**
   * Default: Zacht antraciet - meest gebruikt
   * Gebruik: Standaard icons in UI
   */
  default: {
    weight: 'regular' as IconWeight,
    className: 'text-gray-600',        // Antraciet, zacht contrast
    size: 20,
  },
  
  /**
   * Subtle: Nog zachter - voor achtergrond elementen
   * Gebruik: Minder belangrijke icons, decoratieve elementen
   */
  subtle: {
    weight: 'light' as IconWeight,
    className: 'text-gray-500',        // Zeer subtiel
    size: 20,
  },
  
  /**
   * Emphasis: Iets meer contrast - voor hover/active
   * Gebruik: Hover states, active items
   */
  emphasis: {
    weight: 'regular' as IconWeight,
    className: 'text-gray-700',        // Zachte nadruk
    size: 20,
  },
  
  /**
   * Active/Accent: Oranje - voor acties
   * Gebruik: Call-to-action, belangrijke acties
   */
  active: {
    weight: 'regular' as IconWeight,
    className: 'text-orange-600',      // Warm oranje (niet 500, te fel)
    size: 20,
  },
  
  /**
   * Info: Blauw - voor informatieve elementen
   * Gebruik: Info buttons, help icons
   */
  info: {
    weight: 'regular' as IconWeight,
    className: 'text-blue-600',        // Zacht blauw
    size: 20,
  },
  
  /**
   * Success: Emerald - voor positieve feedback
   * Gebruik: Success messages, checkmarks
   */
  success: {
    weight: 'regular' as IconWeight,
    className: 'text-emerald-600',
    size: 20,
  },
  
  /**
   * Small: Kleinere variant
   * Gebruik: Inline icons, compact UI
   */
  small: {
    weight: 'regular' as IconWeight,
    className: 'text-gray-600',
    size: 16,
  },
  
  /**
   * Large: Grotere variant
   * Gebruik: Headers, feature showcases
   */
  large: {
    weight: 'regular' as IconWeight,
    className: 'text-gray-600',
    size: 24,
  },
} as const;

// ============================================================================
// ICON WEIGHTS - Phosphor's flexibiliteit
// ============================================================================

/**
 * Phosphor biedt 6 gewichten - gebruik ze bewust:
 * - thin: Zeer subtiel (bijna niet gebruiken)
 * - light: Subtiele icons (achtergrond, minder belangrijk)
 * - regular: Standaard (meest gebruikt) ← DEFAULT
 * - bold: Nadruk (hover, active)
 * - fill: Gevuld (alleen voor status indicators)
 * - duotone: Twee-kleuren (niet gebruiken, te druk)
 */
export const ICON_WEIGHTS = {
  subtle: 'light' as IconWeight,
  normal: 'regular' as IconWeight,    // ← STANDAARD, meest gebruikt
  emphasis: 'bold' as IconWeight,
} as const;

// ============================================================================
// GEBRUIK VOORBEELDEN
// ============================================================================

/**
 * VOORBEELD 1: Standaard icon in menu
 * 
 * import { InsightIcon, ICON_STYLES } from './icons/phosphorSystem';
 * 
 * <InsightIcon {...ICON_STYLES.default} />
 * 
 * Output: 20px, regular weight, text-gray-600
 */

/**
 * VOORBEELD 2: Actieve button icon
 * 
 * import { AddIcon, ICON_STYLES } from './icons/phosphorSystem';
 * 
 * <button>
 *   <AddIcon {...ICON_STYLES.active} />
 *   <span>Toevoegen</span>
 * </button>
 * 
 * Output: 20px, regular weight, text-orange-600
 */

/**
 * VOORBEELD 3: Custom size en weight
 * 
 * import { ChatIcon } from './icons/phosphorSystem';
 * 
 * <ChatIcon 
 *   size={24} 
 *   weight="light" 
 *   className="text-gray-500" 
 * />
 */

/**
 * VOORBEELD 4: Inline klein icon
 * 
 * import { ClockIcon, ICON_STYLES } from './icons/phosphorSystem';
 * 
 * <span className="flex items-center gap-1 text-xs text-gray-500">
 *   <ClockIcon {...ICON_STYLES.small} size={14} />
 *   2 uur geleden
 * </span>
 */

// ============================================================================
// MIGRATIE GUIDE - Oud → Nieuw
export const FullscreenIcon = CornersOut;     // Volledig scherm
export const ExitFullscreenIcon = CornersIn; // Volledig scherm sluiten
export const StartMeetingIcon = VideoCameraIcon; // Start Meeting

// ============================================================================

/**
 * LUCIDE → PHOSPHOR MAPPING:
 * 
 * ❌ Sparkles (lucide)       → ✅ InsightIcon (Article)
 * ❌ Bot (lucide)            → ✅ AssistantIcon (ChatCircle)
 * ❌ Brain (lucide)          → ✅ InsightIcon (Article)
 * ❌ Wand2 (lucide)          → ✅ QuickIcon (Lightning)
 * ❌ Zap (lucide)            → ✅ QuickIcon (Lightning)
 * 
 * EMOJI → PHOSPHOR:
 * 
 * ❌ 🤖                      → ✅ RelationIcon (Handshake)
 * ❌ ✨                      → ✅ AccentIcon (Sparkle) - alleen voor rewards!
 * ❌ 💡                      → ✅ IdeaIcon (Lightbulb)
 * ❌ 🎯                      → ✅ GoalIcon (Target)
 * ❌ 📊                      → ✅ AnalyticsIcon (ChartBar)
 * ❌ 💬                      → ✅ ChatIcon (Chat)
 * ❌ 👥                      → ✅ CommunityIcon (Users)
 * ❌ ⏱️                      → ✅ TimeIcon (Timer)
 * ❌ 🧘‍♀️                     → ✅ MindfulIcon (Flower)
 * ❌ 🧠                      → ✅ ThinkIcon (Brain)
 */
